import { XClose } from "@untitled-ui/icons-react";
import { type PointerEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { useVoicePlaybackContext } from "../../store/voicePlayback";
import { formatVoiceDuration } from "../../utils/voice";
import { VoicePlayButton, VoiceSpeedButton } from "./VoiceButtons";

function ratioFromPointer(rect: DOMRect, clientX: number) {
  const rtl = document.documentElement.dir === "rtl";
  const offset = rtl ? rect.right - clientX : clientX - rect.left;
  if (rect.width === 0) return 0;
  return Math.max(0, Math.min(1, offset / rect.width));
}

function VoiceSeekBar({
  progress,
  onSeek,
}: {
  progress: number;
  onSeek: (ratio: number) => void;
}) {
  const { t } = useTranslation("messenger.user");
  const trackRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const ratioRef = useRef(progress);
  const [dragRatio, setDragRatio] = useState<number | null>(null);
  const shown = dragRatio ?? progress;

  const ratioAt = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return shown;
    return ratioFromPointer(rect, clientX);
  };

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    dragging.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    const ratio = ratioAt(event.clientX);
    ratioRef.current = ratio;
    setDragRatio(ratio);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const ratio = ratioAt(event.clientX);
    ratioRef.current = ratio;
    setDragRatio(ratio);
  };

  const finishDrag = () => {
    if (!dragging.current) return;
    dragging.current = false;
    onSeek(ratioRef.current);
    setDragRatio(null);
  };

  return (
    <div
      ref={trackRef}
      role="slider"
      aria-label={t("Voice message")}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(shown * 100)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={finishDrag}
      onPointerCancel={finishDrag}
      onKeyDown={(event) => {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        const step = event.key === "ArrowRight" ? 0.05 : -0.05;
        const rtl = document.documentElement.dir === "rtl";
        onSeek(Math.max(0, Math.min(1, progress + (rtl ? -step : step))));
      }}
      className="relative h-0.5 w-full cursor-pointer touch-none"
    >
      <div className="absolute inset-x-0 -top-[3px] h-2">
        <div className="absolute inset-x-0 top-[3px] h-0.5 rounded-full bg-gray-300">
          <div
            className="bg-primary-600 h-full rounded-full"
            style={{ width: `${shown * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}

export default function VoicePlayerBar({
  compact = false,
}: {
  compact?: boolean;
}) {
  const { t } = useTranslation("messenger.user");
  const active = useVoicePlaybackContext((state) => state.active);
  const closeVoice = useVoicePlaybackContext((state) => state.closeVoice);
  const scrollToActiveVoice = useVoicePlaybackContext(
    (state) => state.scrollToActiveVoice,
  );
  const toggle = useVoicePlaybackContext((state) => state.toggle);
  const seek = useVoicePlaybackContext((state) => state.seek);
  const setRate = useVoicePlaybackContext((state) => state.setRate);
  const isPlaying = useVoicePlaybackContext((state) => state.isPlaying);
  const currentTime = useVoicePlaybackContext((state) => state.currentTime);
  const duration = useVoicePlaybackContext((state) => state.duration);
  const progress = useVoicePlaybackContext((state) => state.progress);
  const rate = useVoicePlaybackContext((state) => state.rate);

  if (!active) return null;

  const time = formatVoiceDuration(currentTime * 1000);

  const openActiveVoice = () => scrollToActiveVoice();

  return (
    <div
      className={cn(
        "flex shrink-0 flex-col items-end bg-white",
        compact ? "pt-0" : "gap-1.5 pt-2",
      )}
    >
      <div
        className={cn(
          "flex h-9 w-full items-center justify-between",
          compact ? "px-4" : "px-6",
        )}
      >
        <div
          className={cn(
            "flex min-w-0 items-center",
            compact ? "gap-0.5" : "gap-2",
          )}
        >
          <span
            className={cn(compact && "flex size-9 items-center justify-center")}
          >
            <VoicePlayButton
              isPlaying={isPlaying}
              onClick={() => toggle()}
              label={isPlaying ? t("Pause") : t("Play")}
              compact={compact}
            />
          </span>
          <button
            type="button"
            onClick={openActiveVoice}
            className="flex min-w-0 cursor-pointer items-center gap-1 text-end"
          >
            <span
              className={cn(
                "truncate font-semibold text-gray-700",
                compact ? "text-xs leading-[18px]" : "text-sm leading-5",
              )}
            >
              {active.userName}
            </span>
            {active.userPosition ? (
              <>
                <span
                  className={cn(
                    "text-gray-600",
                    compact ? "text-xs leading-[18px]" : "text-sm leading-5",
                  )}
                >
                  -
                </span>
                <span
                  className={cn(
                    "truncate font-normal text-gray-600",
                    compact ? "text-xs leading-[18px]" : "text-sm leading-5",
                  )}
                >
                  {active.userPosition}
                </span>
              </>
            ) : null}
          </button>
        </div>

        <div
          className="flex shrink-0 items-center gap-1"
          onClick={(event) => event.stopPropagation()}
        >
          {!compact && (
            <span className="voice-time px-2 text-sm leading-5 font-medium text-gray-600">
              {time}
            </span>
          )}
          <VoiceSpeedButton
            rate={rate}
            onChange={setRate}
            disabled={!duration}
          />
          <button
            type="button"
            onClick={closeVoice}
            aria-label={t("Close", { ns: "core.shared" })}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg text-gray-600 hover:bg-gray-50"
          >
            <XClose className="size-5" />
          </button>
        </div>
      </div>

      <VoiceSeekBar progress={progress} onSeek={seek} />
    </div>
  );
}
